/**
 * QuotaCard — daily + monthly usage gauge for an AI provider (Gemini / Claude).
 *
 * Fetches /api/admin/ai-control/quota/{provider} and renders two progress bars
 * (today vs daily limit, this month vs monthly limit) plus a top-features list.
 * Colors automatically warn (≥70%) and alert (≥90%).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Gauge, RefreshCw, TrendingUp } from 'lucide-react';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  ok:     { bar: 'bg-emerald-400', text: 'text-emerald-300', ring: 'border-emerald-500/30', bg: 'bg-emerald-500/5', Icon: CheckCircle2, labelAr: 'ضمن الحدود', labelEn: 'Within limits' },
  warn:   { bar: 'bg-amber-400',   text: 'text-amber-300',   ring: 'border-amber-500/40',   bg: 'bg-amber-500/5',   Icon: AlertTriangle, labelAr: 'اقتراب من الحد', labelEn: 'Approaching limit' },
  danger: { bar: 'bg-red-500',     text: 'text-red-300',     ring: 'border-red-500/50',     bg: 'bg-red-500/8',     Icon: AlertTriangle, labelAr: 'حرج — تخطيت ٩٠٪', labelEn: 'Critical — over 90%' },
};

function statusForPct(pct) {
  if (pct >= 90) return 'danger';
  if (pct >= 70) return 'warn';
  return 'ok';
}

export default function QuotaCard({ provider, language }) {
  const isAr = language === 'ar';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/ai-control/quota/${provider}`, {
        headers: getAuthHeaders(),
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="bg-white/[0.02] rounded-xl border border-white/10 p-6 flex items-center justify-center" data-testid="quota-card-loading">
        <RefreshCw className="h-5 w-5 animate-spin text-[#d4af37]" />
      </div>
    );
  }
  if (!data) return null;

  const overall = STATUS_STYLES[data.status] || STATUS_STYLES.ok;
  const OverallIcon = overall.Icon;

  return (
    <div
      className={`${overall.bg} rounded-2xl border ${overall.ring} p-5 space-y-5`}
      data-testid={`quota-card-${provider.toLowerCase()}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl ${overall.bg} border ${overall.ring} flex items-center justify-center`}>
            <Gauge className={`h-5 w-5 ${overall.text}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-white truncate">
              {isAr ? `استهلاك ${provider === 'Google' ? 'Gemini' : 'Claude'}` : `${provider === 'Google' ? 'Gemini' : 'Claude'} Quota`}
            </h3>
            <p className="text-[10px] text-[#A1A1AA] truncate">{data.limit_label}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 text-xs ${overall.text} font-semibold`}>
          <OverallIcon className="h-4 w-4" />
          <span>{isAr ? overall.labelAr : overall.labelEn}</span>
          <button
            onClick={load}
            className="ms-2 p-1 rounded hover:bg-white/5 text-[#A1A1AA]"
            data-testid="quota-card-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Two Progress Bars: Daily + Monthly */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GaugeRow
          isAr={isAr}
          label={isAr ? 'اليوم' : 'Today'}
          used={data.today.calls}
          limit={data.today.limit}
          pct={data.today.pct}
          tokens={data.today.tokens}
          cost={data.today.cost}
          errors={data.today.errors}
        />
        <GaugeRow
          isAr={isAr}
          label={isAr ? 'هذا الشهر' : 'This month'}
          used={data.month.calls}
          limit={data.month.limit}
          pct={data.month.pct}
          tokens={data.month.tokens}
          cost={data.month.cost}
          errors={data.month.errors}
        />
      </div>

      {/* Top features this month */}
      {data.top_features && data.top_features.length > 0 && (
        <div>
          <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-2 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            {isAr ? 'أعلى الميزات استهلاكاً هذا الشهر' : 'Top features this month'}
          </p>
          <div className="space-y-1.5">
            {data.top_features.map((f) => (
              <div
                key={f.feature_id}
                className="flex items-center gap-2 text-xs bg-white/[0.02] rounded-lg px-3 py-2 border border-white/5"
              >
                <code className="text-[#d4af37] font-mono text-[10px] flex-1 truncate">{f.feature_id || '—'}</code>
                <span className="text-white tabular-nums">{f.calls.toLocaleString()}</span>
                <span className="text-[10px] text-[#71717A]">{isAr ? 'نداء' : 'calls'}</span>
                <span className="text-[10px] text-blue-300 tabular-nums">{f.tokens.toLocaleString()}t</span>
                {f.cost_usd > 0 && (
                  <span className="text-[10px] text-emerald-300 tabular-nums">${f.cost_usd.toFixed(4)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GaugeRow({ isAr, label, used, limit, pct, tokens, cost, errors }) {
  const safe = Math.min(100, Math.max(0, pct || 0));
  const s = STATUS_STYLES[statusForPct(safe)];
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-[#A1A1AA] font-medium">{label}</span>
        <span className={`${s.text} font-bold tabular-nums`}>
          {used.toLocaleString()} / {limit.toLocaleString()}
          <span className="text-[#71717A] ms-1">({safe}%)</span>
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
        <div
          className={`h-full ${s.bar} transition-all duration-700 ease-out`}
          style={{ width: `${safe}%` }}
        />
      </div>
      <div className="flex items-center gap-3 text-[10px] text-[#71717A] tabular-nums">
        <span>{tokens.toLocaleString()} {isAr ? 'توكن' : 'tokens'}</span>
        {cost > 0 && <span>${cost.toFixed(4)}</span>}
        {errors > 0 && <span className="text-red-300">{errors} {isAr ? 'خطأ' : 'errors'}</span>}
      </div>
    </div>
  );
}
